/**
 * MapView — MapLibre GL JS wrapper for the OEM Home Dashboard.
 *
 * Ported from `design/gripp-os/apps/web/src/pages/home/maps/MapView.tsx`.
 *
 * Key adaptations for Gripp/React-Native-Web:
 * - Basemap palette mapped to Crop Circle design tokens (slate-100 for ocean in
 *   light mode, neutral-800 for ocean in dark mode) instead of hardcoded hex.
 * - Container uses inline styles via StyleSheet for RNW compatibility; the
 *   inner <div> for MapLibre still uses className so MapLibre's own CSS rules
 *   (which target .maplibregl-map) continue to work.
 * - `useDarkMode` observes `.dark` on <html> (same as reference).
 * - Web-only — do NOT import this from mobile code.
 */

import { tokens } from '@gripp/shared-ui';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

/** One row in a marker's click popup, rendered as "{value} {label}". */
export interface MapMarkerDetail {
  label: string;
  value: string;
}

export interface MapMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
  /** Dot fill color (hex or CSS color). */
  color: string;
  /** Status bucket — used to aggregate severity counts inside a cluster. */
  bucket?: 'urgent' | 'pending' | 'solved';
  status?: string;
  meta?: string;
  /** Whether to render a pulsing ring around the dot. */
  pulse?: boolean;
  /**
   * Rollup rows shown in a popup when the marker is clicked (e.g. a location's
   * Activities / Assets / Routines counts). Only used in the non-clustered
   * (individual-marker) render path.
   */
  details?: MapMarkerDetail[];
}

export interface MapViewProps {
  /** Initial/controlled center [lng, lat]. */
  center: [number, number];
  /** Initial/controlled zoom level. */
  zoom: number;
  markers?: MapMarker[];
  selectedMarkerId?: string | null;
  onMarkerClick?: (marker: MapMarker) => void;
  /** Extra control rendered above the zoom buttons in the controls pill. */
  extraControl?: ReactNode;
  /**
   * When true, the map automatically frames the markers' bounding box (instead
   * of staying at `center`/`zoom`) whenever the marker set changes — so a
   * region-clustered set zooms to that region rather than showing the globe.
   * Falls back to `center`/`zoom` when there are no markers.
   */
  fitToMarkers?: boolean;
  /**
   * When true, render markers as a clustered GeoJSON source (circle + count
   * layers) instead of individual DOM markers. Nearby points collapse into a
   * single "(x)" bubble that splits apart as you zoom in. Click a cluster to
   * zoom to its members; click a lone point for a label popup.
   */
  clustered?: boolean;
}

// ---------------------------------------------------------------------------
// Basemap palette — derived from Crop Circle tokens so the map visually
// integrates with the app theme. Comments show the token name.
// ---------------------------------------------------------------------------
const LIGHT_BASEMAP = {
  ocean: tokens.colors.slate[100],     // slate-100 — cool off-white sea
  land: tokens.colors.neutral[100],    // neutral-100 — white/almost-white land
  border: tokens.colors.neutral[300],  // neutral-300 — light border between countries
};

const DARK_BASEMAP = {
  ocean: tokens.colors.neutral[800],   // neutral-800 — near-black sea
  land: tokens.colors.brand.surface,   // brand-surface — slightly lighter land surface
  border: tokens.colors.brand.layer,   // brand-layer — subtle border
};

// No glyph source: the map uses NO MapLibre symbol/text layers, so it needs no
// external (or self-hosted) glyph PBFs. The only text on the map — cluster count
// labels — is rendered as DOM markers (see createClusterCountElement), keeping
// the map free of any third-party runtime dependency.

// Self-hosted world basemap geometry: Natural Earth 1:50m admin-0 country
// polygons (public domain), stripped to geometry-only + coords rounded to 2dp,
// shipped as a static asset in `public/maps/`, so the simplistic country-fill
// basemap needs NO external tile/vector server. (1:50m
// over 1:110m gives noticeably finer coastlines — e.g. the Great Lakes region.)
const WORLD_GEOJSON_URL = '/maps/world-countries-50m.json';

// Self-hosted lakes (Natural Earth 1:50m), painted in the ocean colour on top
// of land. The admin-0 country polygons don't cut lakes out, so without this
// layer the Great Lakes / Caspian / etc. render as land. Drawn under borders.
const LAKES_GEOJSON_URL = '/maps/lakes-50m.json';

// Self-hosted US state outlines: Natural Earth 1:50m admin-1 states/provinces,
// filtered to the US (50 states + DC), geometry-only. Drawn as a subtle line
// layer that only appears once zoomed into the country (see US_STATES_MINZOOM)
// so the world view stays clean — country borders only.
const US_STATES_GEOJSON_URL = '/maps/us-states-50m.json';
// State lines start fading in here and reach full strength ~1.5 zoom levels
// later. The whole-world default sits at ~1.5, so they're hidden until you zoom
// into North America.
const US_STATES_MINZOOM = 3;

// GeoJSON source + layer ids for the clustered rendering path.
const CLUSTER_SRC = 'activity-points';
const CLUSTER_LAYER = 'clusters';

// Pixel radius within which points collapse into one cluster. This is the
// "area of the dot as it renders" the design intends: dots that would visually
// crowd each other merge, and they separate again as you zoom in (supercluster
// re-clusters per zoom level). Tune to taste — larger = chunkier clusters.
const CLUSTER_RADIUS_PX = 40;

// How much to pull back from the tight marker-bounds fit, in zoom levels. One
// zoom level doubles the scale, so log2(1.1) ≈ 0.14 widens the view ~10% (more
// "outside" visible) while keeping every marker comfortably in frame.
const FIT_ZOOM_OUT = 0.14;

// The bottom card strip floats over the lower ~260px of the map. Frame markers
// into the band ABOVE it by padding the fit asymmetrically (large bottom inset)
// so no pin hides behind the cards. The inset clears the strip height PLUS the
// marker's label pill, which is vertically centered on the dot and so dips
// ~14px below it — at a flush 260 the bottom-most label slips behind the cards.
// Top/sides stay generous so points sit well inside the viewport.
const FIT_PADDING = { top: 72, right: 96, bottom: 300, left: 96 };
// Single/coincident point can't form a bounds box — flyTo + this upward pixel
// offset lifts it into the same clear band above the strip.
const SINGLE_POINT_OFFSET: [number, number] = [0, -110];

// Builds the home-dashboard basemap style: a self-hosted, Crop-Circle-themed
// vector map (country fills + lakes + zoom-gated US state lines). This is the
// only basemap the dashboard uses — the old CartoCDN raster street/satellite
// variants were removed as unused.
function tileStyle(dark: boolean): maplibregl.StyleSpecification {
  const c = dark ? DARK_BASEMAP : LIGHT_BASEMAP;
  return {
    version: 8 as const,
    projection: { type: 'mercator' as const },
    sources: {
      world: {
        type: 'geojson' as const,
        // Self-hosted Natural Earth country polygons (see WORLD_GEOJSON_URL).
        // No external tile/vector server — works offline, no SLA dependency.
        // Provenance: © Natural Earth (naturalearthdata.com), public domain. No
        // `attribution` field: we render no attribution control (see Map ctor),
        // so the string would be dead config; the credit lives here as a comment.
        data: WORLD_GEOJSON_URL,
      },
      lakes: { type: 'geojson' as const, data: LAKES_GEOJSON_URL },
      'us-states': { type: 'geojson' as const, data: US_STATES_GEOJSON_URL },
    },
    // Draw order: ocean → land → lakes (ocean colour) → country borders →
    // US state lines (top, zoom-gated). GeoJSON sources have no
    // `source-layer`; each FeatureCollection is the whole layer.
    layers: [
      {
        id: 'ocean-bg',
        type: 'background' as const,
        paint: { 'background-color': c.ocean },
      },
      {
        id: 'land',
        type: 'fill' as const,
        source: 'world',
        paint: { 'fill-color': c.land },
      },
      {
        id: 'lakes',
        type: 'fill' as const,
        source: 'lakes',
        paint: { 'fill-color': c.ocean },
      },
      {
        id: 'borders',
        type: 'line' as const,
        source: 'world',
        paint: { 'line-color': c.border, 'line-width': 0.5 },
      },
      {
        id: 'us-states',
        type: 'line' as const,
        source: 'us-states',
        // Hidden on the world view; fades in as you zoom into the country so
        // state lines never clutter the global overview.
        minzoom: US_STATES_MINZOOM,
        paint: {
          'line-color': c.border,
          'line-width': 0.5,
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            US_STATES_MINZOOM,
            0,
            US_STATES_MINZOOM + 1.5,
            0.5,
          ],
        },
      },
    ],
  } as maplibregl.StyleSpecification;
}

/**
 * Observes the `.dark` class on `<html>` and returns a reactive boolean,
 * allowing the map to re-initialise when the theme toggle flips.
 */
function useDarkMode(): boolean {
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const update = () => setDark(html.classList.contains('dark'));
    update();
    const obs = new MutationObserver(update);
    obs.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  return dark;
}

// ---------------------------------------------------------------------------
// Marker element builder — shared by the plain DOM-marker path and the
// clustered path's individual (unclustered) points, so a lone point always
// shows its original label pill.
//
// Built with DOM APIs (NOT innerHTML): `name` is the issue title —
// tenant-controlled data — so it MUST be assigned via textContent, never
// interpolated into an HTML string, or a crafted title (e.g. `<img onerror=…>`)
// executes as XSS. `color` is one of our own severity tokens, safe in a style.
// ---------------------------------------------------------------------------
function createMarkerElement(opts: {
  name: string;
  color: string;
  isSelected?: boolean;
  pulse?: boolean;
  /**
   * When true, the label pill captures pointer events and shows a pointer
   * cursor, so clicking the (large, obvious) pill — not just the 14px dot —
   * triggers the marker click. Used for clickable location pins; left off for
   * plain issue labels so they don't swallow map drags.
   */
  interactive?: boolean;
}): HTMLDivElement {
  const {
    name,
    color,
    isSelected = false,
    pulse = false,
    interactive = false,
  } = opts;

  const el = document.createElement('div');
  el.className = 'gripp-map-marker';

  const inner = document.createElement('div');
  inner.className = `gripp-marker-inner${isSelected ? ' is-selected' : ''}`;
  inner.style.cssText =
    'position:relative;width:14px;height:14px;cursor:pointer;';

  if (pulse) {
    const pulseEl = document.createElement('div');
    pulseEl.className = 'gripp-marker-pulse';
    pulseEl.style.cssText =
      'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:30px;height:30px;border-radius:50%;opacity:0.2;animation:gripp-pulse 2s ease-in-out infinite;pointer-events:none;';
    pulseEl.style.background = color;
    inner.appendChild(pulseEl);
  }

  const dot = document.createElement('div');
  dot.className = 'gripp-marker-dot';
  dot.style.cssText =
    // box-sizing:border-box keeps the dot 14×14 *including* its 2px border, so
    // it stays concentric with the 14×14 inner (and thus the pulse). Without it
    // the default content-box renders the dot 18×18, offsetting it ~2px from the
    // pulse centre. (gripp-os doesn't hit this — Tailwind's base sets border-box
    // globally; these raw createElement nodes don't inherit that reset.)
    'box-sizing:border-box;position:absolute;top:0;left:0;width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.25);transition:transform 180ms cubic-bezier(0.32,0.72,0,1),box-shadow 180ms cubic-bezier(0.32,0.72,0,1);';
  dot.style.background = color;
  inner.appendChild(dot);

  const nameWrap = document.createElement('div');
  nameWrap.className = 'gripp-marker-name';
  nameWrap.style.cssText =
    'position:absolute;left:calc(100% + 6px);top:50%;transform:translateY(-50%);padding:4px 10px;border-radius:999px;background:rgba(2,2,2,0.85);white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.25);' +
    (interactive ? 'pointer-events:auto;cursor:pointer;' : 'pointer-events:none;');
  const nameSpan = document.createElement('span');
  nameSpan.style.cssText =
    'font-family:Inter,sans-serif;font-weight:600;font-size:11px;line-height:14px;color:white;';
  // Safe: textContent never interprets markup.
  nameSpan.textContent = name;
  nameWrap.appendChild(nameSpan);
  inner.appendChild(nameWrap);

  el.appendChild(inner);
  return el;
}

// ---------------------------------------------------------------------------
// Cluster count label — the number shown inside a cluster bubble. Rendered as a
// DOM marker (NOT a MapLibre symbol layer) so the map needs no glyph server: the
// bubble disc is still the `clusters` circle layer, and this just overlays the
// count, centred, with pointer-events off so a click falls through to the circle
// layer's zoom-to-expand handler. `count` is a numeric aggregate (never tenant
// data), but we still assign via textContent for consistency.
// ---------------------------------------------------------------------------
function createClusterCountElement(count: string): HTMLDivElement {
  const t = tokens.typography.label.mediumEmphasised;
  const el = document.createElement('div');
  el.style.cssText =
    `font-family:${t.fontFamily},sans-serif;font-weight:${t.fontWeight};font-size:${t.fontSize}px;line-height:${t.fontSize}px;color:${tokens.colors.neutral[100]};pointer-events:none;user-select:none;`;
  el.textContent = count;
  return el;
}

// ---------------------------------------------------------------------------
// Popup content builder — the rollup card shown when a location marker is
// clicked. Built with DOM APIs (NOT innerHTML) and textContent, same XSS-safe
// discipline as createMarkerElement: `name` and detail values are data, never
// interpolated into an HTML string.
// ---------------------------------------------------------------------------
function createPopupElement(
  name: string,
  details: MapMarkerDetail[]
): HTMLDivElement {
  const root = document.createElement('div');
  root.style.cssText = 'min-width:120px;padding:2px 2px 0;';

  const title = document.createElement('div');
  title.style.cssText =
    'font-family:Inter,sans-serif;font-weight:600;font-size:13px;line-height:18px;' +
    `color:${tokens.colors.neutral[800]};margin-bottom:6px;`;
  title.textContent = name;
  root.appendChild(title);

  for (const detail of details) {
    const row = document.createElement('div');
    row.style.cssText =
      'font-family:Inter,sans-serif;font-weight:400;font-size:12px;line-height:18px;' +
      `color:${tokens.colors.neutral[600]};`;
    const value = document.createElement('span');
    value.style.cssText = `font-weight:600;color:${tokens.colors.neutral[800]};`;
    value.textContent = detail.value;
    row.appendChild(value);
    // " Activities" — the leading space separates value from label.
    row.appendChild(document.createTextNode(` ${detail.label}`));
    root.appendChild(row);
  }

  return root;
}

// ---------------------------------------------------------------------------
// Build the clustered GeoJSON source data from the marker list. Shared by the
// cluster source's initial `data` and its later `setData` updates so both stay
// in sync.
// ---------------------------------------------------------------------------
function markersToFeatureCollection(
  markers: MapMarker[]
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: markers.map((m) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [m.lng, m.lat] },
      properties: {
        id: m.id,
        title: m.name,
        color: m.color,
        bucket: m.bucket ?? 'solved',
      },
    })),
  } as GeoJSON.FeatureCollection;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MapView({
  center,
  zoom,
  markers = [],
  selectedMarkerId,
  onMarkerClick,
  extraControl,
  fitToMarkers = false,
  clustered = false,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerInstancesRef = useRef<maplibregl.Marker[]>([]);
  // Per-point DOM markers in clustered mode, keyed by point id, so an
  // unclustered point shows its original label pill (not a bare dot).
  const singleMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  // Per-cluster DOM count labels keyed by cluster_id, overlaid on the circle
  // bubbles. Replaces the old symbol/text layer so no glyph server is needed.
  const clusterCountMarkersRef = useRef<Map<string, maplibregl.Marker>>(
    new Map()
  );
  // Single open popup (the clicked marker's rollup card), so a new click
  // replaces the previous popup instead of stacking them.
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const dark = useDarkMode();
  // Latest onMarkerClick, held in a ref so the clustered single-marker click
  // handlers (wired inside the [mapReady, clustered] effect, which does NOT
  // depend on onMarkerClick) always invoke the current callback rather than a
  // stale closure.
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;

  // --------------------------------------------------------------------------
  // Initialise map. Re-runs when the theme flips so the basemap swaps.
  // DOM markers persist across re-inits; track layers live in the style and
  // are automatically re-drawn when `mapReady` flips back to `true`.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: tileStyle(dark),
      center,
      zoom,
      // No attribution badge: the basemap is fully self-hosted Natural Earth
      // (public domain — attribution requested, not legally required) and the
      // glyph endpoint only serves fonts, so there's no provider mandating the
      // "ℹ" control. Dropping it keeps the OEM canvas clean, edge-to-edge.
      attributionControl: false,
    });

    map.on('load', () => {
      // Give the container a chance to be laid out before the first resize so
      // tiles fill the div correctly (same workaround as reference).
      setTimeout(() => map.resize(), 100);
      setMapReady(true);
    });

    mapRef.current = map;

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark]);

  // --------------------------------------------------------------------------
  // Fly to new center/zoom when props change.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({ center, zoom, duration: 1200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1], zoom]);

  // --------------------------------------------------------------------------
  // Render markers. Clears and re-adds on every `markers` change or when
  // `mapReady` flips so markers appear after map re-init.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear previous marker instances + any open popup. Done BEFORE the
    // clustered bail so a false→true `clustered` toggle tears down the DOM
    // markers this effect rendered (otherwise they'd linger under the
    // GeoJSON layers with stale click handlers).
    markerInstancesRef.current.forEach((m) => m.remove());
    markerInstancesRef.current = [];
    popupRef.current?.remove();
    popupRef.current = null;

    // Clustered mode renders via GeoJSON layers (separate effect), not DOM
    // markers — bail so we don't draw both.
    if (clustered) return;

    markers.forEach((marker) => {
      const hasDetails = !!marker.details && marker.details.length > 0;
      const el = createMarkerElement({
        name: marker.name,
        color: marker.color,
        isSelected: selectedMarkerId === marker.id,
        pulse: marker.pulse,
        interactive: !!onMarkerClick || hasDetails,
      });

      if (onMarkerClick || hasDetails) {
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          onMarkerClick?.(marker);
          const map = mapRef.current;
          if (hasDetails && map) {
            // Replace any open popup with this marker's rollup card.
            popupRef.current?.remove();
            popupRef.current = new maplibregl.Popup({
              offset: 16,
              closeButton: true,
              closeOnClick: true,
            })
              .setLngLat([marker.lng, marker.lat])
              .setDOMContent(
                createPopupElement(marker.name, marker.details ?? [])
              )
              .addTo(map);
          }
        });
      }

      const map = mapRef.current;
      if (!map) return;
      const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([marker.lng, marker.lat])
        .addTo(map);

      markerInstancesRef.current.push(instance);
    });
  }, [markers, mapReady, selectedMarkerId, onMarkerClick, clustered]);

  // --------------------------------------------------------------------------
  // Clustered rendering: a GeoJSON source with `cluster: true`. Clusters render
  // as a single constant-colored bubble with a count; nearby points (within
  // CLUSTER_RADIUS_PX at the current zoom) collapse into one bubble and split
  // apart as you zoom in. Individual (unclustered) points are NOT drawn as bare
  // dots — they render as the original labeled DOM markers (dot + name pill),
  // synced from the source on every move/zoom so labels appear as soon as a
  // point stands alone. Clicking a cluster zooms to expand it.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!clustered || !map || !mapReady) return;

    // Fresh setup: clear any stale DOM markers that belonged to a now-destroyed
    // map instance (post-style-reinit) before re-adding the source.
    singleMarkersRef.current.forEach((m) => m.remove());
    singleMarkersRef.current.clear();
    clusterCountMarkersRef.current.forEach((m) => m.remove());
    clusterCountMarkersRef.current.clear();

    // Reconcile the DOM markers against the source's current features. Two kinds:
    //  - Unclustered leaves (no `point_count`) → labeled dot + name pill.
    //  - Clusters (have `point_count`) → a count label overlaid on the circle
    //    bubble (the disc itself is the CLUSTER_LAYER circle; only the number is
    //    a DOM marker, so no glyph server is needed).
    // Add new ones, refresh/reposition existing ones (re-clustering can move a
    // centroid or change a tally), and drop any that have since merged, split,
    // or scrolled out of the loaded tiles.
    const syncDomMarkers = () => {
      if (!map.getSource(CLUSTER_SRC)) return;
      const feats = map.querySourceFeatures(CLUSTER_SRC);
      const seenSingles = new Set<string>();
      const seenClusters = new Set<string>();
      for (const f of feats) {
        const geom = f.geometry as GeoJSON.Point;
        const coords = geom.coordinates as [number, number];

        if (f.properties?.cluster) {
          const cid = String(f.properties?.cluster_id ?? '');
          if (!cid || seenClusters.has(cid)) continue;
          seenClusters.add(cid);
          const count = String(
            f.properties?.point_count_abbreviated ??
              f.properties?.point_count ??
              ''
          );
          const existing = clusterCountMarkersRef.current.get(cid);
          if (existing) {
            existing.setLngLat(coords);
            const node = existing.getElement();
            if (node.textContent !== count) node.textContent = count;
            continue;
          }
          const el = createClusterCountElement(count);
          const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat(coords)
            .addTo(map);
          clusterCountMarkersRef.current.set(cid, instance);
          continue;
        }

        const id = String(f.properties?.id ?? '');
        if (!id || seenSingles.has(id)) continue;
        seenSingles.add(id);
        if (singleMarkersRef.current.has(id)) continue;
        const [mLng, mLat] = coords;
        const title = String(f.properties?.title ?? '');
        const markerColor = String(
          f.properties?.color ?? tokens.colors.semantic.success
        );
        const el = createMarkerElement({
          name: title,
          color: markerColor,
          // Make the whole label pill clickable (not just the 14px dot) when a
          // click handler is wired, so tapping the asset/activity/product opens
          // its detail page (POC click-to-open, kept atop develop's DOM-marker
          // cluster-count refactor).
          interactive: !!onMarkerClickRef.current,
        });
        if (onMarkerClickRef.current) {
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            onMarkerClickRef.current?.({
              id,
              name: title,
              lng: mLng,
              lat: mLat,
              color: markerColor,
            });
          });
        }
        const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat(coords)
          .addTo(map);
        singleMarkersRef.current.set(id, instance);
      }
      for (const [id, inst] of singleMarkersRef.current) {
        if (!seenSingles.has(id)) {
          inst.remove();
          singleMarkersRef.current.delete(id);
        }
      }
      for (const [cid, inst] of clusterCountMarkersRef.current) {
        if (!seenClusters.has(cid)) {
          inst.remove();
          clusterCountMarkersRef.current.delete(cid);
        }
      }
    };

    map.addSource(CLUSTER_SRC, {
      type: 'geojson',
      data: markersToFeatureCollection(markers),
      cluster: true,
      clusterRadius: CLUSTER_RADIUS_PX,
      clusterMaxZoom: 16,
    } as maplibregl.GeoJSONSourceSpecification);

    map.addLayer({
      id: CLUSTER_LAYER,
      type: 'circle',
      source: CLUSTER_SRC,
      filter: ['has', 'point_count'],
      paint: {
        // Constant color — clusters are a neutral count bubble, not a status
        // signal. Individual points keep their own severity color.
        'circle-color': tokens.colors.neutral[700],
        // Bubble grows in steps with member count.
        'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 50, 26],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.92,
      },
    } as maplibregl.LayerSpecification);

    // Click a cluster → zoom to the level where it expands.
    const onClusterClick = (e: maplibregl.MapLayerMouseEvent) => {
      const feats = map.queryRenderedFeatures(e.point, {
        layers: [CLUSTER_LAYER],
      });
      const clusterId = feats[0]?.properties?.cluster_id as number | undefined;
      if (clusterId == null) return;
      const src = map.getSource(CLUSTER_SRC) as maplibregl.GeoJSONSource;
      src
        .getClusterExpansionZoom(clusterId)
        .then((zoom) => {
          const geom = feats[0].geometry as GeoJSON.Point;
          map.easeTo({
            center: geom.coordinates as [number, number],
            zoom: zoom + 0.3,
            duration: 500,
          });
        })
        .catch(() => {
          /* expansion zoom unavailable — ignore */
        });
    };
    map.on('click', CLUSTER_LAYER, onClusterClick);

    const setPointer = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const clearPointer = () => {
      map.getCanvas().style.cursor = '';
    };
    map.on('mouseenter', CLUSTER_LAYER, setPointer);
    map.on('mouseleave', CLUSTER_LAYER, clearPointer);

    // Re-sync DOM markers whenever clustering recomputes (source data settles)
    // or the view changes (pan/zoom changes which points are alone vs. clustered
    // and where each cluster centroid sits).
    const onSourceData = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId === CLUSTER_SRC && map.isSourceLoaded(CLUSTER_SRC)) {
        syncDomMarkers();
      }
    };
    map.on('sourcedata', onSourceData);
    map.on('moveend', syncDomMarkers);
    syncDomMarkers();

    // Teardown — runs when clustering turns OFF (card deselected / filter
    // X-ed out), the map re-inits (theme swap), or the component unmounts.
    // WITHOUT this, the cluster bubbles + their layer/source/handlers and the
    // DOM markers (point labels + cluster counts) linger on top of the resting
    // Location dots. Guarded because the map may already be gone (unmount /
    // theme reinit), in which case getLayer/removeLayer would throw.
    return () => {
      try {
        map.off('click', CLUSTER_LAYER, onClusterClick);
        map.off('mouseenter', CLUSTER_LAYER, setPointer);
        map.off('mouseleave', CLUSTER_LAYER, clearPointer);
        map.off('sourcedata', onSourceData);
        map.off('moveend', syncDomMarkers);
        if (map.getLayer(CLUSTER_LAYER)) map.removeLayer(CLUSTER_LAYER);
        if (map.getSource(CLUSTER_SRC)) map.removeSource(CLUSTER_SRC);
      } catch {
        /* map already torn down (theme reinit / unmount) — nothing to detach */
      }
      // Both refs are stable refs holding mutable Maps of MapLibre markers (not
      // React-rendered nodes), mutated by syncDomMarkers throughout the effect's
      // life — so cleanup must read their LIVE value, not a setup-time snapshot.
      // The exhaustive-deps ref warning is a false positive here.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const single = singleMarkersRef.current;
      single.forEach((m) => m.remove());
      single.clear();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const clusters = clusterCountMarkersRef.current;
      clusters.forEach((m) => m.remove());
      clusters.clear();
    };
    // `markers` intentionally omitted — data updates live in the effect below so
    // a marker change re-clusters in place instead of rebuilding the source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, clustered]);

  // --------------------------------------------------------------------------
  // Clustered rendering — data sync. Pushes the latest markers into the
  // existing cluster source WITHOUT rebuilding it, so search/range changes
  // re-cluster smoothly instead of flashing. The bound sourcedata/moveend
  // handlers re-sync the labeled single-point markers. No-op until the
  // lifecycle effect above has created the source.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!clustered || !map || !mapReady) return;
    const src = map.getSource(CLUSTER_SRC) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    src.setData(markersToFeatureCollection(markers));
  }, [markers, mapReady, clustered]);

  // --------------------------------------------------------------------------
  // Frame the markers' bounding box. A single (or coincident) point can't form
  // a box, so center+zoom on it instead. `maxZoom` keeps a tightly-clustered
  // set from zooming to street level.
  // --------------------------------------------------------------------------
  const frameMarkers = (duration: number) => {
    const map = mapRef.current;
    if (!map || markers.length === 0) return;
    // The first fit can run before the deferred post-load resize (see the init
    // effect), so the camera would be computed against a stale/smaller canvas
    // and frame the markers too low (bottom pins slip behind the card strip).
    // resize() reads the container's real size synchronously, so cameraForBounds
    // below always uses the true viewport. Subsequent fits are no-ops here.
    map.resize();
    const bounds = new maplibregl.LngLatBounds();
    markers.forEach((m) => bounds.extend([m.lng, m.lat]));
    if (bounds.getNorthEast().distanceTo(bounds.getSouthWest()) < 1) {
      // Single / coincident point: center on it (lifted above the card strip),
      // but don't zoom in hard so it has context around it.
      map.flyTo({
        center: [markers[0].lng, markers[0].lat],
        zoom: 9,
        duration,
        offset: SINGLE_POINT_OFFSET,
      });
      return;
    }
    // Asymmetric padding (large bottom inset) frames the markers into the band
    // above the bottom card strip; a conservative maxZoom keeps a tight cluster
    // from slamming to street level. Compute the fit camera, then ease to a
    // slightly lower zoom (FIT_ZOOM_OUT) so ~10% more of the surrounding map
    // stays visible.
    const camera = map.cameraForBounds(bounds, {
      padding: FIT_PADDING,
      maxZoom: 10,
    });
    if (camera && typeof camera.zoom === 'number') {
      map.easeTo({
        center: camera.center,
        zoom: camera.zoom - FIT_ZOOM_OUT,
        duration,
      });
    } else {
      map.fitBounds(bounds, { padding: FIT_PADDING, duration, maxZoom: 10 });
    }
  };

  // --------------------------------------------------------------------------
  // Auto-fit to markers when enabled. Runs once the map is ready and whenever
  // the marker set changes, so the view frames the data's region instead of
  // sitting on the default world view.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!fitToMarkers || !mapReady) return;
    frameMarkers(600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, mapReady, fitToMarkers]);

  // --------------------------------------------------------------------------
  // Controls — zoom in / zoom out / fit-to-markers
  // --------------------------------------------------------------------------
  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleFitView = () => frameMarkers(800);

  return (
    <View style={styles.container}>
      {/* Inline <style> for CSS animations + MapLibre overrides. */}
      <style>{`
        @keyframes gripp-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.2; }
          50%       { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
        .maplibregl-map    { width: 100% !important; height: 100% !important; }
        .maplibregl-canvas { width: 100% !important; height: 100% !important; }
        .gripp-marker-inner:hover .gripp-marker-dot {
          transform: scale(1.3);
        }
        .gripp-marker-inner.is-selected .gripp-marker-dot {
          transform: scale(1.45);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.95), 0 0 0 5px rgba(2,2,2,0.18), 0 2px 6px rgba(0,0,0,0.3) !important;
        }
        .gripp-marker-inner.is-selected:hover .gripp-marker-dot {
          transform: scale(1.55);
        }
        .maplibregl-popup-content {
          border-radius: 10px;
          padding: 12px 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.16);
        }
        .maplibregl-popup-close-button {
          font-size: 16px;
          padding: 2px 7px;
          color: ${tokens.colors.neutral[500]};
        }
      `}</style>

      {/* MapLibre container — must be a plain div so MapLibre can write its own
          canvas inside it. Positioned absolute to fill the RNW View. */}
      <div
        ref={containerRef}
        style={{ position: 'absolute', inset: 0 }}
      />

      {/* Controls pill — top-right corner, z-ordered above the map. */}
      <div style={controlsPillStyle}>
        {extraControl && (
          <>
            {extraControl}
            <div style={dividerStyle} />
          </>
        )}

        <button
          onClick={handleZoomIn}
          aria-label="Zoom in"
          style={controlButtonStyle}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
              tokens.colors.neutral[200])
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')
          }
        >
          <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
            <path
              d="M0 8H16M8 0V16"
              stroke={tokens.colors.neutral[700]}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div style={dividerStyle} />

        <button
          onClick={handleZoomOut}
          aria-label="Zoom out"
          style={controlButtonStyle}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
              tokens.colors.neutral[200])
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')
          }
        >
          <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
            <path
              d="M0 8H16"
              stroke={tokens.colors.neutral[700]}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {markers.length > 0 && (
          <>
            <div style={dividerStyle} />
            <button
              onClick={handleFitView}
              aria-label="Fit to view"
              title="Fit to view"
              style={controlButtonStyle}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  tokens.colors.neutral[200])
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'transparent')
              }
            >
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 2H4a2 2 0 0 0-2 2v2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 2h2a2 2 0 0 1 2 2v2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 10v2a2 2 0 0 0 2 2h2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 10v2a2 2 0 0 1-2 2h-2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 200,
  },
});

/** Inline CSS object for the controls pill (plain DOM element). */
const controlsPillStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: `${tokens.colors.brand.white}E6`, // ~90% opacity
  backdropFilter: 'blur(4px)',
  borderRadius: 8,
  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
  overflow: 'hidden',
};

const controlButtonStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  display: 'grid',
  placeItems: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  transition: 'background-color 150ms',
};

const dividerStyle: React.CSSProperties = {
  height: 1,
  backgroundColor: tokens.colors.neutral[300],
};
